import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { RoyaltiesContextService } from '../../context/royalties-context.service';
import { RoyaltiesService } from '../../services/royalties.service';
import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Router } from '@angular/router';

@Component({
    selector: 'app-mantenimiento-royalties',
    templateUrl: './mantenimiento-royalties.component.html'
})
export class MantenimientoRoyaltiesComponent implements OnInit, OnDestroy {

  private readonly _DEFAULT_ITEMS_PER_PAGE = 10;

  private _filters;
  public filtersVisible = true;
  public filtersForm: UntypedFormGroup;

  public newEditModal = false;
  public deleteModal = false;

  public mensajes = new Array<Message>();
  public showError: boolean;
  public showWarning: boolean;

  public toCloseSpinner = 0;

  public entidadesControl;
  public royalties;
  public royaltyId;
  public paises;
  public denominaciones;
  public marcas;
  public disabledEntidadControl: Subject<boolean> = new Subject();
  public disabledPaises: Subject<boolean> = new Subject();
  public disabledDenominaciones: Subject<boolean> = new Subject();
  public disabledMarcas: Subject<boolean> = new Subject();

  public unidadesMedida;

  public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
  public currentPage = 0;
  public totalItems: number;
  public readonly visiblePages = 3;

  public orderBy: string;
  public columnId: number;

  private readonly destroy$ = new Subject<void>();

  constructor(
      private readonly _router: Router,
      private _fb: UntypedFormBuilder,
      private readonly _spinnerService: SpinnerService,
      private readonly _sessionContext: SessionContextService,
      private readonly _dataProvider: DataProvider,
      private readonly _royaltiesContext: RoyaltiesContextService,
      private readonly _royaltiesService: RoyaltiesService,
      private readonly _translate: TranslateService
  ) { }

  ngOnInit() {
      this.filtersForm = this._fb.group({
          entidadControl: ['', Validators.required],
          pais: [{ value: '', disabled: true }],
          denominacion: [{ value: '', disabled: true }],
          marca: [{ value: '', disabled: true }]
      });

      this.filtersForm.get('entidadControl').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((entidadControl) => {
        if (entidadControl) {
          this.getPaisesByEntidadControl(entidadControl.id);
        } else{
          this.disabledPaises.next(true);
          this.paises = [];
          this.filtersForm.get('pais').patchValue('');
          this.filtersForm.get('pais').disable();
        }
      });

      this.filtersForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
        if (pais) {
          this.getDenominacionesByPaisEntidadControl(pais.id);
        } else {
          this.disabledDenominaciones.next(true);
          this.denominaciones = [];
          this.filtersForm.get('denominacion').patchValue('');
          this.filtersForm.get('denominacion').disable();
        }
      });

      this.filtersForm.get('denominacion').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((denominacion) => {
        if (denominacion) {
          this.getMarcasByPaisEntidadControlDeno(denominacion.id);
        } else {
          this.disabledMarcas.next(true);
          this.marcas = [];
          this.filtersForm.get('marca').patchValue('');
          this.filtersForm.get('marca').disable();
        }
      });

      this.getEntidadesControl();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkWarnings(entidadControl) {
    if (entidadControl && this.filtersForm.valid) {
        this.filtersVisible = false;
        this.showWarning = false;
        this.mensajes = [];
        this.getRoyalties();
    } else {
        this.filtersVisible = true;
        this.showWarning = true;
        this.mensajes.push({ class: 'warning', message: 'global.info.filterRequired' });
    }
  }

  public getEntidadesControl(){
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._royaltiesService.getEntidadControlAll().toPromise().finally(() => {
      this.checkCloseSpinner();
    }).then((resp) => {
      if (resp.length > 0) {
        this.entidadesControl = resp;
        if (this.entidadesControl && this.entidadesControl.length == 1) {
          this.filtersForm.get("entidadControl").patchValue(this.entidadesControl[0]);
          this.filtersForm.get("entidadControl").disable({onlySelf: true, emitEvent: false});
        }
        this.checkWarnings(this.filtersForm.get('entidadControl').value);
      }
    });
  }

  public getPaisesByEntidadControl(entidadControlId){
    let filters = {
      entidadControlId: entidadControlId
    };
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._royaltiesService.getPaisesByEntidadControl(filters).toPromise().finally(() => {
      this.checkCloseSpinner();
    }).then((resp) => {
      if (resp.lista.length > 0) {
        this.paises = resp.lista;
        if (this.paises.length === 1) {
          this.filtersForm.get('pais').patchValue(this.paises[0]);
          this.filtersForm.get('pais').disable();
          this.disabledPaises.next(true);
        } else {
          this.filtersForm.get('pais').enable();
          this.disabledPaises.next(false);
        }
      }
    });
  }

  public getDenominacionesByPaisEntidadControl(paisId){
    let filters = {
      paisId: paisId,
      entidadControlId: this.filtersForm.get('entidadControl').value.id
    };
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._royaltiesService.getDenominacionesByPaisEntidadControl(filters).toPromise().finally(() => {
      this.checkCloseSpinner();
    }).then((resp) => {
      if (resp.lista.length > 0) {
        this.denominaciones = resp.lista;
        if (this.denominaciones.length === 1) {
          this.filtersForm.get('denominacion').patchValue(this.denominaciones[0]);
          this.filtersForm.get('denominacion').disable();
          this.disabledDenominaciones.next(true);
        } else {
          this.filtersForm.get('denominacion').enable();
          this.disabledDenominaciones.next(false);
        }
      }
    });
  }

  public getMarcasByPaisEntidadControlDeno(denominacionId){
    let filters = {
      paisId: this.filtersForm.get('pais').value.id,
      entidadControlId: this.filtersForm.get('entidadControl').value.id,
      denominacionId: denominacionId
    };
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._royaltiesService.getMarcasByPaisEntidadControlDeno(filters).toPromise().finally(() => {
      this.checkCloseSpinner();
    }).then((resp) => {
      if (resp.lista.length > 0) {
        this.marcas = resp.lista;
        this.filtersForm.get('marca').enable();
        this.disabledMarcas.next(false);
      }
    });
  }

  public get isSmallScreen(){
    return this._sessionContext.isSmallScreen();
  }

  public get unidadesPeso() {
    return this._dataProvider.unidadesPesoRoyalties;
  }

  public get unidadesDivisas() {
    return this._dataProvider.unidadesDivisasRoyalties;
  }

  public get unidadesSuperficie() {
    return this._dataProvider.unidadesSuperficieRoyalties;
  }

  public get unidadesNPlantas() {
    return this._dataProvider.unidadesNPlantasRoyalties;
  }

  public get defaultLanguage() {
    return this._translate.getDefaultLang();
  }

  public getUnidadesMedida(){
    const entidadControl = this.filtersForm.get('entidadControl').value;
    this.unidadesMedida = {
      peso: this.unidadesPeso.find(a=> a.id === entidadControl.unidadMedidaId),
      divisas: this.unidadesDivisas.find(a=> a.id === entidadControl.divisaId),
      superficie: this.unidadesSuperficie.find(a=> a.id === entidadControl.uM_SuperficieId),
      nPlantas: this.unidadesNPlantas.find(a=> a.id === entidadControl.uM_NPlantasId),
    };
  }

  public getRoyalties(){
    if (this._royaltiesContext.mantenimientoRoyaltiesFilters) {
      this._filters = this._royaltiesContext.mantenimientoRoyaltiesFilters;
    } else {
      this.applyFilters();
    }
    this.getUnidadesMedida();
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._royaltiesService.getRoyalties(this._filters).toPromise().finally(() => {
      this.checkCloseSpinner();
    }).then((resp) => {
      this.totalItems = resp.paginacionMeta.totalCount;
      this.royalties = resp.lista;
    }, (error) => {
      this.pushMessage('global.error.noResponse');
    });
  }

  public pageChange(page) {
    this._royaltiesContext.mantenimientoRoyaltiesFilters.numPagina = this.currentPage = page - 1;
    this.getRoyalties();
  }

  public itemsPerPageChange(items) {
    this._royaltiesContext.mantenimientoRoyaltiesFilters.numPagina = this.currentPage = 0;
    this._royaltiesContext.mantenimientoRoyaltiesFilters.tamPagina = this.itemsPerPage = items;
    this.getRoyalties();
  }

  public checkValuesForm(): boolean {
    return (this.filtersForm.get('entidadControl').value ||
      this.filtersForm.get('pais').value ||
      this.filtersForm.get('denominacion').value ||
      this.filtersForm.get('marca').value) ? true : false;
  }

  public resetForm() {
    this.filtersForm.reset({
      entidadControl: this.filtersForm.get('entidadControl').disabled ? this.filtersForm.get('entidadControl').value : null
    });
  }

  public filtrar() {
    if (this.filtersForm.invalid) {
      this.mensajes = [];
      this.showError = true;
      this.mensajes.push({ class: 'error', message: 'global.error.required' });
    } else if (this.filtersForm.valid) {
      this.applyFilters();
    }
  }

  private applyFilters() {
    this.currentPage = 0;
    this._royaltiesContext.mantenimientoRoyaltiesFilters = {
      entidadControlId: this.filtersForm.get('entidadControl').value ? this.filtersForm.get('entidadControl').value.id : '',
      paisId: this.filtersForm.get('pais').value ? this.filtersForm.get('pais').value.id : '',
      denominacionId: this.filtersForm.get('denominacion').value ? this.filtersForm.get('denominacion').value.id : '',
      marcaId: this.filtersForm.get('marca').value ? this.filtersForm.get('marca').value.id : '',
      numPagina: this.currentPage,
      tamPagina: this.itemsPerPage
    };
    this.getRoyalties();
  }

  public create() {
    this.royaltyId = null;
    this.newEditModal = true;
  }

  public update(royalty) {
    this.royaltyId = royalty.id;
    this.newEditModal = true;
  }

  public askDelete(royalty) {
    this.royaltyId = royalty.id;
    this.deleteModal = true;
  }

  public delete() {
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._royaltiesService.deleteRoyalty(this.royaltyId).toPromise().finally(() => {
      this.checkCloseSpinner();
    }).then(() => {
      this.close();
      this.pushMessage('global.success.itemDeleted');
      this.getRoyalties();
    });
  }

  public close() {
    this.deleteModal = false;
    this.newEditModal = false;
  }

  public refresh(value) {
    this.newEditModal = false;
    if (value) {
      this.checkWarnings(this.filtersForm.get('entidadControl').value);
    }
  }

  public pushMessage(mensaje) {
    this.mensajes = [];
    this.showError = true;
    if (mensaje.includes('error') || mensaje.includes('ApiErrors')) {
      this.mensajes.push({ class: 'error', message: mensaje });
    } else {
      this.mensajes.push({ class: 'success', message: mensaje });
    }
  }

  public getIcon(prop: string) {
    let iconClass = 'fa fa-sort';
    if (this._filters && this._filters.orderItems && this._filters.orderItems[0].orderProperty === prop) {
        iconClass = this._filters.orderItems[0].orderByDescending === true ? 'fa fa-sort-down' : 'fa fa-sort-up';
    }
    return iconClass;
  }

  public toOrderBy(columnFilter, idColumn) {
    this.columnId = idColumn;
    if (this.royalties && this.royalties.length > 0) {
      if (this._royaltiesContext.mantenimientoRoyaltiesFilters) {
        this._filters = this._royaltiesContext.mantenimientoRoyaltiesFilters;
        if (!this.orderBy || this.orderBy.includes('DESC')) {
          this.orderBy = 'ASC' + idColumn;
          this._filters.orderItems = [
            {
              orderProperty: columnFilter,
              orderByDescending: false
            }
          ];
        } else if (this.orderBy.includes('ASC')) {
          this.orderBy = 'DESC' + idColumn;
          this._filters.orderItems = [
            {
              orderProperty: columnFilter,
              orderByDescending: true
            }
          ];
        }
        this.getRoyalties();
      }
    }
  }

  private checkCloseSpinner() {
    this.toCloseSpinner -= 1;
    if (this.toCloseSpinner === 0) {
        this._spinnerService.hideSpinner();
    }
  }

  public openDocHelp() {
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._royaltiesService.getDocHelp('Configuration_Royalties.pdf').toPromise().finally(() => {
        this.checkCloseSpinner();
    }).then((resp) => {
        localStorage.setItem('base64', resp.base64Archivo);
        const url = this._router.serializeUrl(this._router.createUrlTree(['viewPdf']));
        window.open(url, '_blank');
    });
  }

  public trackByRoyaltyId(index: number, royalty: { id: number }): number {
    return royalty.id;
  }
}
